import type { CustomerSearchRow } from '@plato/search/search-types';
import { SearchEntity } from '@plato/search/search-types';
import { LinkText } from '@plato/ui-legacy/components/ui/link-text';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomerDTO } from '../../../../server/dto/customer-dto.ts';
import type { EntitySearchComboboxProps } from './entity-search-combobox.tsx';
import { EntitySearchCombobox } from './entity-search-combobox.tsx';

export type CustomerSelectProps = {
    customer?: CustomerDTO;
    value: string | undefined;
    onChange: (customerId: string, customer: CustomerSearchRow) => void;
    onClear?: () => void;
    includeLink?: boolean;
} & Omit<EntitySearchComboboxProps, 'entities' | 'defaultValue' | 'onValueChange' | 'onClear'>;

export const CustomerSelect = React.forwardRef<HTMLDivElement, CustomerSelectProps>(
    ({ value, onChange, onClear, customer, includeLink, inputPlaceholder, ...props }, ref) => {
        const { t } = useTranslation();

        return (
            <div ref={ref} className="flex flex-col gap-2 w-full items-stretch">
                <EntitySearchCombobox
                    entities={[SearchEntity.Customer]}
                    inputPlaceholder={
                        inputPlaceholder ??
                        t('placeholder__select_customer', {
                            ns: 'common',
                        })
                    }
                    defaultValue={
                        customer
                            ? [
                                  {
                                      id: customer.customerId,
                                      name: customer.customerName ?? '',
                                      entity: SearchEntity.Customer,
                                  },
                              ]
                            : undefined
                    }
                    onValueChange={(value) => {
                        const [item] = value;
                        const { original } = item ?? {};

                        if (original?.entity === SearchEntity.Customer) {
                            onChange(original.id, original);
                        }
                    }}
                    onClear={onClear}
                    {...props}
                />
                {includeLink && value && (
                    <div className="px-2">
                        <LinkText
                            href={`/customers/${encodeURIComponent(value)}`}
                            className="text-sm text-blue-600"
                        >
                            {t('view_customer', { ns: 'common' })}
                        </LinkText>
                    </div>
                )}
            </div>
        );
    },
);

CustomerSelect.displayName = 'CustomerSelect';
